import * as THREE from 'three';
import { FIXED_DT, type EngineContext, type FrameTime, type GameModule, type RenderSystem } from '@/contracts';
import { Bus } from './events';
import { Input } from './input';
import { SettingsStore, TIER_DPR } from './settings';
import { Assets } from './assets';
import { FrameStats, type PerfSnapshot } from './perf';
import { Jobs } from './jobs';
import { PreloadManager } from './preload';
import { LongTaskLog, devPerfEnabled } from './longtask';
import { QualityGovernor, type GovernorState } from './governor';

/**
 * Never advance more than this many fixed steps in one frame. Past the cap we
 * let simulation time slip rather than entering a death spiral where each frame
 * takes longer than the time it is trying to catch up on.
 */
const MAX_STEPS = 5;

/** Share of the loading bar given to module init()s; the rest is preload phases. */
const INIT_PROGRESS_SHARE = 0.6;

/** Frame budget the whole tick aims at; leftover time feeds the job scheduler. */
const FRAME_BUDGET_MS = 16.7;

/** Job scheduler budget bounds per frame (docs/preload-design.md §2.5). */
const JOBS_MIN_MS = 1;
const JOBS_MAX_MS = 4;

/** Dev program-census window: shader compiles in the first N frames after boot
 *  mean the warm-up missed a permutation (docs/preload-design.md §2.2). */
const CENSUS_FRAMES = 120;

export class Engine implements EngineContext {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly events = new Bus();
  readonly settings: SettingsStore;
  readonly input: Input;
  readonly assets = new Assets();
  /** Time-sliced mid-session work queue, stepped after the render each frame. */
  readonly jobs = new Jobs();

  private readonly modules: GameModule[] = [];
  private readonly byName = new Map<string, GameModule>();
  private readonly fixed: GameModule[] = [];
  private readonly framed: GameModule[] = [];
  private render?: RenderSystem;
  private readonly longtasks = new LongTaskLog();
  private readonly governor: QualityGovernor;
  private readonly offQualityResize: () => void;

  private accumulator = 0;
  private last = 0;
  private raf = 0;
  private booted = false;
  private paused = false;
  private censusLeft = 0;
  private censusBase = -1;
  private readonly time: FrameTime = { elapsed: 0, delta: 0, steps: 0, alpha: 0 };

  /**
   * Permanent frame-time instrument: wall / CPU / GPU percentiles over a rolling
   * window. Public so the perf overlay, the auto-quality governor and the
   * screenshot harness all read the same numbers.
   */
  readonly perf = new FrameStats();

  constructor(mount: HTMLElement) {
    this.canvas = document.createElement('canvas');
    mount.appendChild(this.canvas);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false,          // handled in post; MSAA is wasted on a deferred-ish chain
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
      alpha: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.info.autoReset = false;

    this.camera = new THREE.PerspectiveCamera(58, 1, 0.25, 4000);
    this.camera.position.set(24, 14, 32);
    this.camera.lookAt(0, 2, 0);

    this.settings = new SettingsStore(this.events);
    this.input = new Input(this.canvas, this.settings);
    this.governor = new QualityGovernor(this.settings, this.perf, this.events, () => this.resize());
    if (devPerfEnabled()) this.longtasks.start();

    this.resize();
    addEventListener('resize', this.resize);
    document.addEventListener('visibilitychange', this.onVisibility);
    // The per-tier dpr cap and preset renderScale must reach the framebuffer
    // whenever the tier changes, whoever changed it.
    this.offQualityResize = this.events.on('quality:changed', () => this.resize());
  }

  /** Modules must be added before `boot()`. */
  add(m: GameModule): this {
    if (this.booted) throw new Error(`[engine] cannot add "${m.name}" after boot`);
    if (this.byName.has(m.name)) throw new Error(`[engine] duplicate module "${m.name}"`);
    this.modules.push(m);
    this.byName.set(m.name, m);
    return this;
  }

  get<T extends GameModule>(name: string): T {
    const m = this.byName.get(name);
    if (!m) throw new Error(`[engine] module "${name}" is not registered`);
    return m as T;
  }

  tryGet<T extends GameModule>(name: string): T | undefined {
    return this.byName.get(name) as T | undefined;
  }

  async boot(onProgress?: (frac: number, label: string) => void): Promise<void> {
    this.modules.sort((a, b) => a.order - b.order);
    for (let i = 0; i < this.modules.length; i++) {
      const m = this.modules[i];
      onProgress?.(INIT_PROGRESS_SHARE * (i / this.modules.length), m.name);
      await m.init(this);
      // Yield to the browser so the loading screen actually repaints between modules.
      await new Promise((r) => requestAnimationFrame(r));
    }

    for (const m of this.modules) {
      if (typeof m.fixedUpdate === 'function') this.fixed.push(m);
      if (typeof m.update === 'function') this.framed.push(m);
    }
    this.render = this.tryGet<RenderSystem>('render');

    // Preload phases (docs/preload-design.md §2.2): every module registers its
    // warm-up work, then the manager drains it behind the loading screen —
    // ending with the shader warm-up rig, so boot does not resolve (and the
    // loading screen does not lift) until every program the session needs is
    // compiled.
    const preload = new PreloadManager();
    for (const m of this.modules) m.preload?.(this, preload);
    await preload.run(this.render, (frac, phase) => {
      onProgress?.(INIT_PROGRESS_SHARE + (1 - INIT_PROGRESS_SHARE) * frac, `phase:${phase}`);
    });

    onProgress?.(1, 'ready');
    // Bind the GPU timer after boot: the context and all its extensions exist by
    // now, and nothing before this point has a frame worth timing.
    this.perf.attach(this.renderer);
    this.longtasks.markSession();
    if (devPerfEnabled()) this.censusLeft = CENSUS_FRAMES;
    this.booted = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  setPaused(p: boolean): void {
    if (this.paused === p) return;
    this.paused = p;
    this.accumulator = 0;
    this.events.emit('sim:paused', { paused: p });
  }

  get isPaused(): boolean { return this.paused; }
  get frameTimeMs(): number { return this.perf.frameMs; }

  /** One-call read of the whole perf picture, for the overlay and the harness. */
  perfSnapshot(): PerfSnapshot {
    const s = this.perf.snapshot(this.renderer.info);
    if (this.longtasks.active) {
      s.longTasks = this.longtasks.count;
      s.longTasksBoot = this.longtasks.bootCount;
      s.longTaskWorstMs = this.longtasks.worstMs;
    }
    return s;
  }

  /** Auto-quality governor state, for the perf overlay and console debugging. */
  governorState(): GovernorState { return this.governor.state(); }

  private readonly tick = (now: number): void => {
    this.raf = requestAnimationFrame(this.tick);
    this.perf.beginFrame(now);
    const cpuStart = performance.now();

    // Clamp: a long stall (tab switch, GC, breakpoint) must not teleport the world.
    const rawDelta = (now - this.last) / 1000;
    this.last = now;
    const delta = Math.min(rawDelta, 0.25);

    this.time.elapsed += delta;
    this.time.delta = delta;

    this.input.poll();

    let steps = 0;
    if (!this.paused) {
      this.accumulator += delta;
      while (this.accumulator >= FIXED_DT && steps < MAX_STEPS) {
        for (const m of this.fixed) m.fixedUpdate!(FIXED_DT);
        this.accumulator -= FIXED_DT;
        steps++;
      }
      if (steps === MAX_STEPS) this.accumulator = 0; // give up on the backlog
    }
    this.time.steps = steps;
    this.time.alpha = this.accumulator / FIXED_DT;

    for (const m of this.framed) m.update!(this.time);

    this.renderer.info.reset();
    if (this.render) this.render.render(this.time);
    else this.renderer.render(this.scene, this.camera);

    // Dev program census: any shader compiled after boot is a warm-up miss.
    if (this.censusLeft > 0) {
      const progs = this.renderer.info.programs?.length ?? 0;
      if (this.censusBase < 0) {
        this.censusBase = progs;
      } else if (progs > this.censusBase) {
        console.error(`[engine] ${progs - this.censusBase} shader program(s) compiled after boot (${this.censusBase} → ${progs}) — the warm-up rig is missing a permutation`);
        this.censusBase = progs;
      }
      this.censusLeft--;
    }

    // Leftover frame time feeds the mid-session job scheduler: at least 1 ms of
    // progress per frame, at most ~4 ms, shrinking as the frame itself gets hot.
    const cpuSpent = performance.now() - cpuStart;
    this.jobs.frame(Math.max(JOBS_MIN_MS, Math.min(JOBS_MAX_MS, FRAME_BUDGET_MS - cpuSpent)));
    if (!this.paused) this.governor.frame(now);

    this.perf.endFrame(this.renderer.info);
    this.input.endFrame();
  };

  private readonly onVisibility = (): void => {
    // Auto-pause when hidden: rAF is throttled anyway, and children walk away.
    if (document.hidden) this.setPaused(true);
    else this.last = performance.now();
  };

  private readonly resize = (): void => {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    const scale = this.settings.renderScale;
    // Per-tier dpr ceiling (docs/perf-plan.md item 1): pixel count is the
    // biggest iGPU lever. Only ultra keeps native retina; captures run dpr 1.
    const dpr = Math.min(window.devicePixelRatio || 1, TIER_DPR[this.settings.quality]) * scale;
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / Math.max(1, h);
    this.camera.far = Math.max(1200, this.settings.drawDistance * 1.6);
    this.camera.updateProjectionMatrix();
  };

  /** Public so the settings UI can re-apply after a render-scale change. */
  applyViewport(): void { this.resize(); }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    removeEventListener('resize', this.resize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.offQualityResize();
    this.governor.dispose();
    this.longtasks.dispose();
    for (const m of this.modules) m.dispose?.();
    this.perf.dispose();
    this.input.dispose();
    this.assets.disposeAll();
    this.renderer.dispose();
    this.events.clear();
  }
}
